import { Check, Loader2, Plus, PlusCircle, XCircle } from "lucide-react"
import * as React from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { TagActionMenu } from "./TagActionMenu"

export interface FacetedFilterOption {
  label: string
  value: string
  icon?: React.ComponentType<{ className?: string }>
  count?: number
  onRename?: (value: string) => Promise<void> | void
  onDelete?: () => Promise<void> | void
  /** Splits the list into groups: a divider is drawn wherever the group changes
   *  between two visible options, so a search that hides some keeps the split. */
  group?: string
}

interface FacetedFilterProps {
  title: string
  options: FacetedFilterOption[]
  selected: string[]
  onSelectionChange: (values: string[]) => void
  onCreateOption?: (value: string) => Promise<void> | void
  createOptionLabel?: (value: string) => string
  isCreatingOption?: boolean
  showSelectedCountOnly?: boolean
  /** When active, style the trigger with a primary border/text instead of showing a selected-count badge. */
  compactCount?: boolean
  hideLeadingIcon?: boolean
  leadingIcon?: React.ComponentType<{ className?: string }>
  triggerClassName?: string
  disabled?: boolean
  /**
   * When provided, the caller owns filtering — `options` is assumed to
   * already match the current search text (e.g. a server-side query), so
   * the component's local substring filter is skipped. Calls fire debounced
   * (300ms) as the user types.
   */
  onQueryChange?: (query: string) => void
  /** Paired with `onLoadMore`: renders a trailing "Load more" row when there are more results than the currently loaded `options`. */
  hasMore?: boolean
  onLoadMore?: () => void
  isLoadingMore?: boolean
}

export function FacetedFilter({
  title,
  options,
  selected,
  onSelectionChange,
  onCreateOption,
  createOptionLabel,
  isCreatingOption = false,
  showSelectedCountOnly = false,
  compactCount = false,
  hideLeadingIcon = false,
  leadingIcon: LeadingIcon = PlusCircle,
  triggerClassName,
  disabled = false,
  onQueryChange,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
}: FacetedFilterProps) {
  const { translate } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const selectedSet = React.useMemo(() => new Set(selected), [selected])
  const trimmedQuery = query.trim()
  const normalizedQuery = trimmedQuery.toLocaleLowerCase()

  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleQueryChange = React.useCallback(
    (value: string) => {
      setQuery(value)
      if (!onQueryChange) return
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => onQueryChange(value), 300)
    },
    [onQueryChange],
  )

  const filteredOptions = React.useMemo(() => {
    // Caller already scoped `options` to the current search (server-side) —
    // filtering again locally would hide rows that don't literally match
    // client-side but are legitimately present for other reasons.
    if (onQueryChange) return options
    if (normalizedQuery.length === 0) {
      return options
    }

    return options.filter((option) => {
      const label = option.label.toLocaleLowerCase()
      const value = option.value.toLocaleLowerCase()

      return label.includes(normalizedQuery) || value.includes(normalizedQuery)
    })
  }, [normalizedQuery, options, onQueryChange])

  const canCreateOption =
    onCreateOption != null &&
    trimmedQuery.length > 0 &&
    filteredOptions.length === 0

  const onItemSelect = React.useCallback(
    (option: FacetedFilterOption, isSelected: boolean) => {
      const newSelected = new Set(selectedSet)
      if (isSelected) {
        newSelected.delete(option.value)
      } else {
        newSelected.add(option.value)
      }
      onSelectionChange(Array.from(newSelected))
    },
    [selectedSet, onSelectionChange],
  )

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)
      if (!nextOpen) {
        setQuery("")
        if (debounceRef.current) clearTimeout(debounceRef.current)
        onQueryChange?.("")
      }
    },
    [onQueryChange],
  )

  const handleCreateOption = React.useCallback(async () => {
    if (!onCreateOption || trimmedQuery.length === 0 || isCreatingOption) {
      return
    }

    try {
      await onCreateOption(trimmedQuery)
      setOpen(false)
      setQuery("")
    } catch {
      return
    }
  }, [isCreatingOption, onCreateOption, trimmedQuery])

  const onReset = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation()
      onSelectionChange([])
    },
    [onSelectionChange],
  )

  return (
    <Popover open={disabled ? false : open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className={cn(
            "border-dashed font-normal",
            compactCount &&
              selectedSet.size > 0 &&
              "border-solid border-primary text-primary",
            triggerClassName,
          )}
        >
          {selectedSet.size === 0 && !hideLeadingIcon && (
            <LeadingIcon className="size-4" />
          )}
          {title}
          {selectedSet.size > 0 && !compactCount && (
            <>
              <Separator
                orientation="vertical"
                className="mx-0.5 data-[orientation=vertical]:h-4"
              />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal lg:hidden"
              >
                {selectedSet.size}
              </Badge>
              <div className="hidden items-center gap-1 lg:flex">
                {showSelectedCountOnly || selectedSet.size > 2 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    {selectedSet.size} selected
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedSet.has(option.value))
                    .map((option) => (
                      <Badge
                        variant="secondary"
                        key={option.value}
                        className="rounded-sm px-1 font-normal"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
          {selectedSet.size > 0 && (
            <div
              role="button"
              aria-label={translate("FILTERS/CLEAR_FILTER_ARIA", { title })}
              tabIndex={0}
              className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden"
              onClick={onReset}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  onReset()
                }
              }}
            >
              <XCircle />
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={title}
            value={query}
            onValueChange={handleQueryChange}
          />
          <CommandList className="max-h-full">
            {filteredOptions.length === 0 && !canCreateOption && (
              <CommandEmpty>
                {translate("KNOWLEDGEBASE/TAG_NO_RESULTS")}
              </CommandEmpty>
            )}
            {canCreateOption && (
              <CommandGroup>
                <CommandItem
                  onSelect={() => void handleCreateOption()}
                  disabled={isCreatingOption}
                >
                  <Plus className="size-4" />
                  <span className="truncate">
                    {createOptionLabel?.(trimmedQuery) ??
                      translate("KNOWLEDGEBASE/FILTER_CREATE_OPTION_FALLBACK", {
                        value: trimmedQuery,
                      })}
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
            {filteredOptions.length > 0 && (
              <CommandGroup className="max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto">
                {filteredOptions.map((option, index) => {
                  const isSelected = selectedSet.has(option.value)
                  const hasActions =
                    option.onRename != null || option.onDelete != null

                  return (
                    <React.Fragment key={option.value}>
                      {index > 0 &&
                        option.group !== filteredOptions[index - 1].group && (
                          // cmdk hides separators while a search is typed; this
                          // one marks the group split, which a search keeps.
                          <CommandSeparator alwaysRender className="my-1" />
                        )}
                      <CommandItem
                        onSelect={() => onItemSelect(option, isSelected)}
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          <div
                            className={cn(
                              "flex size-4 items-center justify-center rounded-sm border border-primary",
                              isSelected
                                ? "bg-primary text-primary-foreground"
                                : "opacity-50 [&_svg]:invisible",
                            )}
                          >
                            <Check />
                          </div>
                          {option.icon && <option.icon className="size-4" />}
                          <span className="truncate" title={option.value}>
                            {option.label}
                          </span>
                          {option.count != null && (
                            <span className="font-mono ml-auto text-xs">
                              {option.count}
                            </span>
                          )}
                        </div>
                        {hasActions && (
                          <TagActionMenu
                            label={option.label}
                            onRename={option.onRename}
                            onDelete={option.onDelete}
                          />
                        )}
                      </CommandItem>
                    </React.Fragment>
                  )
                })}
              </CommandGroup>
            )}
            {hasMore && onLoadMore && (
              <CommandGroup>
                <CommandItem
                  onSelect={() => onLoadMore()}
                  disabled={isLoadingMore}
                  className="justify-center text-center"
                >
                  {isLoadingMore ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    "Load more"
                  )}
                </CommandItem>
              </CommandGroup>
            )}
            {selectedSet.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onReset()}
                    className="justify-center text-center"
                  >
                    {/* This clears only THIS facet (see onReset above), never
                        every filter on the page — "Clear all filters" over-
                        claimed that. Reuse the aria-label's already-accurate,
                        facet-scoped wording instead of adding a new key. */}
                    {translate("FILTERS/CLEAR_FILTER_ARIA", { title })}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
